export interface NatalChartPosition {
    key: string
    name: string
    longitude: number
    sign: string
    degreeText: string
}

export interface NatalChartResponse {
    fullName: string
    birthDate: string
    birthTime: string
    birthPlace: string
    timezone: string
    coordinates: { lat: number; lon: number }
    generatedAtUtc: string
    summary: {
        sunSign: string
        moonSign: string
    }
    positions: NatalChartPosition[]
    technical: {
        aspects: Array<{
            from: string
            to: string
            type: string
            angle: number
        }>
        distributions: {
            byElement: Record<string, number>
            byModality: Record<string, number>
            byPolarity: Record<string, number>
        }
    }
}

const MESES_ES = [
    'enero',
    'febrero',
    'marzo',
    'abril',
    'mayo',
    'junio',
    'julio',
    'agosto',
    'septiembre',
    'octubre',
    'noviembre',
    'diciembre',
] as const

/** Año-mes-día civil en una zona IANA (sin dependencias extra). */
function ymdEnZona(timeZone: string, instant: Date): { y: number; m: number; d: number } {
    const dtf = new Intl.DateTimeFormat('en-CA', {
        timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    })
    const parts = dtf.formatToParts(instant)
    const get = (type: Intl.DateTimeFormatPartTypes) => parseInt(parts.find((p) => p.type === type)?.value || '0', 10)
    return { y: get('year'), m: get('month'), d: get('day') }
}

function hoyLargoEnZona(timeZone: string, instant: Date): string {
    return new Intl.DateTimeFormat('es-AR', {
        timeZone,
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric',
    }).format(instant)
}

/** Primer y último mes del ciclo de 12 meses desde el mes calendario actual en esa zona. */
function rangoDoceMesesEnZona(timeZone: string, instant: Date): { mesInicio: string; mesFin: string } {
    const { y, m } = ymdEnZona(timeZone, instant)
    const m0 = m - 1
    const endIdx = m0 + 11
    const endY = y + Math.floor(endIdx / 12)
    const endM0 = ((endIdx % 12) + 12) % 12
    return {
        mesInicio: `${MESES_ES[m0]} de ${y}`,
        mesFin: `${MESES_ES[endM0]} de ${endY}`,
    }
}

export const buildNatalPremiumPrompt = (chart: NatalChartResponse) => {
    let zona = chart.timezone || 'America/Argentina/Buenos_Aires'
    try {
        new Intl.DateTimeFormat('es-AR', { timeZone: zona }).format(new Date())
    } catch {
        zona = 'America/Argentina/Buenos_Aires'
    }
    const now = new Date()
    const hoyLargo = hoyLargoEnZona(zona, now)
    const { mesInicio: mesInicioCiclo, mesFin: mesFinCiclo } = rangoDoceMesesEnZona(zona, now)

    const positions = chart.positions.map((position) => {
        return `${position.name}: ${position.sign} (${position.degreeText})`
    })
    const aspects = chart.technical.aspects.map((aspect) => `${aspect.from} ${aspect.type} ${aspect.to}`)
    const elementSummary = Object.entries(chart.technical.distributions.byElement)
        .map(([key, value]) => `${key}: ${value}`)
        .join(', ')
    const modalitySummary = Object.entries(chart.technical.distributions.byModality)
        .map(([key, value]) => `${key}: ${value}`)
        .join(', ')
    const polaritySummary = Object.entries(chart.technical.distributions.byPolarity)
        .map(([key, value]) => `${key}: ${value}`)
        .join(', ')

    return `Sos astróloga profesional. Redactá un INFORME COMPLETO DE CARTA NATAL tipo consultorio (equivalente a muchas páginas de lectura escrita), para ${chart.fullName}. No resumas: desarrollá cada bloque con varios párrafos, ejemplos de vida cotidiana, matices y matices psicológicos. Evitá listas telegráficas; priorizá prosa rica.

FECHA DE REFERENCIA PARA PROYECCIONES (obligatorio — no inventes años pasados como futuro):
- Momento de esta consulta: ${hoyLargo} (zona horaria del nacimiento: ${zona}).
- En la sección 17 "Próximos 12 meses", el pronóstico debe cubrir exactamente 12 meses consecutivos empezando en ${mesInicioCiclo} y terminando en ${mesFinCiclo} (inclusive). Si usás trimestres, el primero debe empezar en el trimestre calendario que contiene ${mesInicioCiclo}. Prohibido arrancar en 2023, 2024 o 2025 ni cualquier mes ya pasado respecto de ${hoyLargo}.

Datos natales:
- Fecha: ${chart.birthDate}
- Hora: ${chart.birthTime}
- Lugar: ${chart.birthPlace}
- Zona horaria: ${chart.timezone}
- Coordenadas: lat ${chart.coordinates.lat}, lon ${chart.coordinates.lon}
- Sol: ${chart.summary.sunSign}
- Luna: ${chart.summary.moonSign}

Posiciones planetarias:
${positions.join('\n')}

Aspectos principales (usá todos para comentar dinámicas internas y relaciones entre energías):
${aspects.length ? aspects.join('\n') : '(Sin aspectos mayores dentro del orbe habitual — explicá qué implica eso en la lectura.)'}

Distribución astrológica:
- Elementos: ${elementSummary}
- Modalidades: ${modalitySummary}
- Polaridad: ${polaritySummary}

Extensión obligatoria: como mínimo unas 4500-7000 palabras en total (informe largo, estilo “varias hojas”). Si hace falta, seguí desarrollando dentro de cada sección.

Formato obligatorio (cada ítem empieza en una línea nueva exactamente así: "N) Título" con N de 1 a 18, seguido de mucho texto):

1) Carta natal: síntesis ejecutiva (qué motor tiene esta vida y qué busca el alma en esta encarnación)
2) Sol en signo y grado: identidad consciente, ego sano, sombras del sol, cómo brillar sin quemarse
3) Luna en signo y grado: mundo emocional, infancia interior, vínculo con la madre/cuidados, autocuidado
4) Ascendente inferido (si no hay datos de casas, deducí tendencias desde Sol-Luna y planetas angulares por aspectos): cómo se muestra al mundo y primera impresión
5) Mercurio: mente, aprendizaje, comunicación, humor intelectual, conflictos verbales posibles
6) Venus: amor, placer, estética, dinero afectivo, qué busca en la pareja y en la amistad
7) Marte: deseo, límites, coraje, enojo, sexualidad en clave simbólica, dónde compite o se impone
8) Jupiter y Saturno: expansión vs estructura, leyes internas, responsabilidad, karma de crecimiento
9) Urano, Neptuno, Plutón (aunque sean lentos): qué generación toca y cómo se personaliza en esta carta
10) Aspectos mayores en profundidad: recorre los aspectos listados y explicá tensiones y talentos (no solo definiciones)
11) Stellium o concentraciones (si aplica por signo/casa conceptual): excesos y compensaciones
12) Elementos, modalidades y polaridad: cómo se mezcla fuego-tierra-aire-agua en la personalidad y en la salud emocional
13) Relaciones y pareja: dinámica afectiva, celos, confianza, patrones repetidos y sanación
14) Familia, raíces y hogar (eje lunar-saturnino y aspectos): pertenencia y límites familiares
15) Trabajo, vocación y dinero: rumbo profesional, bloqueos, recursos, abundancia
16) Salud simbólica y ritmos (sin diagnosticar): equilibrio, estrés, recomendaciones holísticas generales
17) Próximos 12 meses: mes a mes en párrafos breves o por trimestres (elegí una estructura clara y cumplila), siempre alineado al rango ${mesInicioCiclo}–${mesFinCiclo} indicado arriba
18) Prácticas concretas: ritual, diario, terapia, estudio, ejercicio, límites — al menos 8 acciones medibles en el tiempo

Recordá: tono cercano en castellano rioplatense, sin decir que sos IA, sin disculpas ni advertencias sobre límites del modelo.
No uses encabezados distintos al formato "N) Título" para las secciones principales (así el PDF puede partir el texto).`
}
